import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  TooltipProps
} from "recharts";
import KC_DashboardCard from "../../../../designsystem/KC_Card/KC_DashboardCard";
import { Distribution } from "../../model/AnalyticsData";
import { GRAPH_COLORS } from "../../../../designsystem/KC_Card/constants";
import { tt } from "../../../../utils/i18n/i18n";
import GRAPH_MIN_HEIGHT from "./constants";

interface Props {
  distribution?: Distribution[];
}

export default function ParticipationChart(props: Props) {
  const chartData = useMemo(() => {
    if (!props.distribution) {
      return [];
    }
    return props.distribution
      .sort((a, b) => a._id.localeCompare(b._id))
      .map((item, index) => ({
        name: item._id,
        value: item.count,
        fill: GRAPH_COLORS[index % GRAPH_COLORS.length]
      }));
  }, [props.distribution]);

  const yAxisDomain = useMemo(() => {
    if (chartData.length === 0) {
      return [0, "auto"];
    }

    const maxValue = Math.max(...chartData.map((d) => d.value));

    if (maxValue === 0) {
      return [0, 10];
    }

    const roundTo = maxValue < 10 ? 5 : maxValue < 50 ? 10 : 50;
    const yAxisMax = Math.ceil(maxValue / roundTo) * roundTo;

    return [0, yAxisMax];
  }, [chartData]);

  return (
    <KC_DashboardCard title={tt("participation_distribution")}>
      <ResponsiveContainer
        width="100%"
        height="100%"
        minHeight={GRAPH_MIN_HEIGHT}
        debounce={500}
      >
        <BarChart
          data={chartData}
          margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
        >
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 14, fill: "#6b7280" }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 14, fill: "#9ca3af" }}
            domain={yAxisDomain as [number, number | string]}
          />
          <Tooltip cursor={false} content={CustomTooltip} />
          <Bar dataKey="value" radius={[8, 8, 8, 8]} />
        </BarChart>
      </ResponsiveContainer>
    </KC_DashboardCard>
  );
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<any, any>) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <div
      className="custom-tooltip"
      style={{
        backgroundColor: "#fff",
        padding: "10px",
        border: "1px solid #ccc",
        borderRadius: "4px"
      }}
    >
      {payload.map((entry, index) => (
        <p key={index} style={{ color: entry.color }}>
          {entry.value > 1 &&
            tt("analytics_participation_plural", {
              value: entry.value,
              label
            })}
          {entry.value <= 1 &&
            tt("analytics_participation_singular", {
              value: entry.value,
              label
            })}
        </p>
      ))}
    </div>
  );
};
